import { AfterViewInit, Component, Inject, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { UiAtomButtonComponent } from '../../atoms/ui-atom-button/ui-atom-button.component';
import { UiMoleculeInputComponent } from '../ui-molecule-input/ui-molecule-input.component';
import { UiAtomTextComponent } from '../../atoms/ui-atom-text/ui-atom-text.component';
import { MatSelectModule } from '@angular/material/select';
import { User } from '@core/services/use-cases/user.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

const USER_FIRST_NAME_MAX = 50;
const USER_LAST_NAME_MAX = 100;
const USER_EMAIL_MIN = 6;
const USER_EMAIL_MAX = 320;
const USER_PASSWORD_MIN = 12;
const USER_PASSWORD_MAX = 64;

export interface AdminUpsertData {
  title: string;
  message?: string;
  defaults?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: string;
    password?: string;
  };
  availableRoles: ('SUPER_ADMIN' | 'COUNTRY_ADMIN' | 'PARTNER_USER')[];
  context: 'global' | 'country' | 'partner';
  countryId?: string;
  partnerId?: string;
  mode?: 'create' | 'edit';
  user?: User;
}

@Component({
  selector: 'app-ui-molecule-dialog-admin-upsert',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatSelectModule,
    MatIconModule,
    UiAtomButtonComponent,
    UiMoleculeInputComponent,
    UiAtomTextComponent,
    MatDialogClose,
    TranslatePipe,
  ],
  templateUrl: './ui-molecule-dialog-admin-upsert.component.html',
  styleUrl: './ui-molecule-dialog-admin-upsert.component.scss',
})
export class UiMoleculeDialogAdminUpsertComponent implements AfterViewInit {
  readonly firstNameMax = USER_FIRST_NAME_MAX;
  readonly lastNameMax = USER_LAST_NAME_MAX;
  readonly emailMin = USER_EMAIL_MIN;
  readonly emailMax = USER_EMAIL_MAX;
  readonly passwordMin = USER_PASSWORD_MIN;
  readonly passwordMax = USER_PASSWORD_MAX;
  form: FormGroup;
  hidePassword = true;
  formSubmitted = false;

  /** Determina si se debe mostrar el selector de rol */
  get showRoleSelector(): boolean {
    return this.data.availableRoles.length > 1;
  }

  @ViewChildren(UiAtomButtonComponent) buttons!: QueryList<UiAtomButtonComponent>;

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<UiMoleculeDialogAdminUpsertComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AdminUpsertData,
  ) {
    this.form = this.fb.group({
      firstName: [
        data.defaults?.firstName ?? '',
        [Validators.required, Validators.maxLength(USER_FIRST_NAME_MAX)],
      ],
      lastName: [
        data.defaults?.lastName ?? '',
        [Validators.required, Validators.maxLength(USER_LAST_NAME_MAX)],
      ],
      email: [
        data.defaults?.email ?? '',
        [
          Validators.required,
          Validators.email,
          Validators.minLength(USER_EMAIL_MIN),
          Validators.maxLength(USER_EMAIL_MAX),
        ],
      ],
      role: [data.defaults?.role ?? this.getDefaultRole(), [Validators.required]],
      password: [
        data.defaults?.password ?? '',
        data.mode === 'edit'
          ? []
          : [
              Validators.required,
              Validators.minLength(USER_PASSWORD_MIN),
              Validators.maxLength(USER_PASSWORD_MAX),
            ],
      ],
    });
  }

  private shouldShow(controlName: string): boolean {
    const c = this.form.get(controlName);
    return !!c && (c.touched || c.dirty || this.formSubmitted);
  }

  get firstNameError(): string | null {
    if (!this.shouldShow('firstName')) return null;
    const c = this.form.get('firstName')!;
    if (c.hasError('required')) return this.translate.instant('users.upsert.firstName.error.required');
    if (c.hasError('maxlength')) return this.translate.instant('users.upsert.firstName.error.maxLength', { max: this.firstNameMax });
    return null;
  }

  get lastNameError(): string | null {
    if (!this.shouldShow('lastName')) return null;
    const c = this.form.get('lastName')!;
    if (c.hasError('required')) return this.translate.instant('users.upsert.lastName.error.required');
    if (c.hasError('maxlength')) return this.translate.instant('users.upsert.lastName.error.maxLength', { max: this.lastNameMax });
    return null;
  }

  get emailError(): string | null {
    if (!this.shouldShow('email')) return null;
    const c = this.form.get('email')!;
    if (c.hasError('required')) return this.translate.instant('users.upsert.email.error.required');
    if (c.hasError('email')) return this.translate.instant('users.upsert.email.error.invalid');
    if (c.hasError('minlength')) return this.translate.instant('users.upsert.email.error.minLength', { min: this.emailMin });
    if (c.hasError('maxlength')) return this.translate.instant('users.upsert.email.error.maxLength', { max: this.emailMax });
    return null;
  }

  get passwordError(): string | null {
    if (!this.shouldShow('password')) return null;
    const c = this.form.get('password')!;
    if (c.hasError('required')) return this.translate.instant('users.upsert.password.error.required');
    if (c.hasError('minlength')) return this.translate.instant('users.upsert.password.error.minLength', { min: this.passwordMin });
    if (c.hasError('maxlength')) return this.translate.instant('users.upsert.password.error.maxLength', { max: this.passwordMax });
    return null;
  }

  private getDefaultRole(): string {
    switch (this.data.context) {
      case 'global':
        return 'SUPER_ADMIN';
      case 'country':
        return 'COUNTRY_ADMIN';
      case 'partner':
        return 'PARTNER_USER';
      default:
        return 'COUNTRY_ADMIN';
    }
  }

  ngAfterViewInit(): void {
    if (this.buttons && this.buttons.length > 0) {
      this.buttons.first.focus();
    }
  }

  submit(): void {
    this.formSubmitted = true;
    if (this.form.valid) {
      const formData = this.form.value;
      // Construir el payload según OpenAPI: countryId, partnerId, email, firstName, lastName, password
      const basePayload: any = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
      };

      // Añadir countryId según contexto
      if (this.data.context === 'country' && this.data.countryId) {
        basePayload.countryId = this.data.countryId;
      }

      // Añadir solo partnerId para usuarios partner
      if (this.data.context === 'partner' && this.data.partnerId) {
        basePayload.partnerId = this.data.partnerId;
      }

      // Añadir password solo si está presente o si es modo create
      if (this.data.mode === 'edit') {
        // En modo edición, solo incluir password si se proporciona
        if (formData.password) {
          basePayload.password = formData.password;
        }
        this.dialogRef.close({ mode: 'edit', payload: basePayload, userId: this.data.user?.id });
      } else {
        // En modo create, siempre incluir password
        basePayload.password = formData.password;
        this.dialogRef.close({ mode: 'create', payload: basePayload });
      }
    } else {
      this.form.markAllAsTouched();
    }
  }

  togglePasswordVisibility(): void {
    this.hidePassword = !this.hidePassword;
  }

  get roleDisplayText(): string {
    switch (this.data.context) {
      case 'global':
        return 'Super Admin';
      case 'country':
        return 'Country Admin';
      case 'partner':
        return 'Partner Admin';
      default:
        return 'Admin';
    }
  }

  getRoleDisplayName(role: string): string {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'Super Admin';
      case 'COUNTRY_ADMIN':
        return 'Country Admin';
      case 'PARTNER_USER':
        return 'Partner Admin';
      default:
        return role;
    }
  }

  getContextInfoText(): string {
    switch (this.data.context) {
      case 'global':
        return 'This user will have access to all countries and partners in the system.';
      case 'country':
        return 'This user will have access to all partners within the selected country.';
      case 'partner':
        return 'This user will have access only to this specific partner and its beneficiaries.';
      default:
        return '';
    }
  }
}
